import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

// Confetti Container
const ConfettiContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10000;
  overflow: hidden;
`;

// Confetti Fall Animation
const confettiFall = keyframes`
  0% {
    transform: translateY(0) translateX(0) rotate(0deg);
    opacity: 1;
  }
  15% {
    opacity: 1;
  }
  100% {
    transform: translateY(-120vh) translateX(var(--drift)) rotate(var(--rotation));
    opacity: 0;
  }
`;

// Individual Confetti Piece
const ConfettiPiece = styled.div`
  position: absolute;
  width: var(--size);
  height: var(--size);
  background: var(--color);
  opacity: 0;
  animation: ${confettiFall} var(--duration) var(--delay) cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  transform-origin: center;
  border-radius: var(--radius);
  will-change: transform, opacity;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

/**
 * Confetti Component
 * 
 * A beautiful, customizable confetti animation that shoots from the bottom corners
 * and spreads naturally across the screen.
 * 
 * @param {Object} props
 * @param {string[]} props.colors - Array of color codes for confetti pieces (default: vibrant pastel colors)
 * @param {number} props.pieceCount - Total number of confetti pieces (default: 200)
 * @param {number} props.duration - Duration range in seconds [min, max] (default: [4, 6])
 * @param {number} props.spawnDuration - How long confetti spawns in seconds (default: 2)
 * @param {number} props.spread - Horizontal spread multiplier (default: 1)
 * @param {string[]} props.shapes - Array of shapes: 'circle', 'square', 'rectangle' (default: all three)
 */
const Confetti = ({
  colors = [
    "#7dd3c0",
    "#5bc5d4",
    "#6ee7b7",
    "#fbbf24",
    "#f87171",
    "#a78bfa",
    "#f472b6",
    "#60a5fa",
    "#34d399",
    "#fbbf24",
  ],
  pieceCount = 200,
  duration = [4, 6],
  spawnDuration = 2,
  spread = 1,
  shapes = ["circle", "square", "rectangle"],
}) => {
  const [pieces, setPieces] = useState([]);

  useEffect(() => {
    const createConfettiPiece = (startX, velocityX, delay) => {
      const color = colors[Math.floor(Math.random() * colors.length)];
      const shape = shapes[Math.floor(Math.random() * shapes.length)];

      // Size variation
      const size = 8 + Math.random() * 8; // 8-16px

      // Duration with variation
      const pieceDuration = duration[0] + Math.random() * (duration[1] - duration[0]);

      // Horizontal drift with spread multiplier
      const drift = velocityX * (140 + Math.random() * 100) * spread;

      // Rotation
      const rotation = Math.random() * 720 - 360; // -360 to 360 degrees

      // Opacity
      const opacity = 0.7 + Math.random() * 0.3;

      // Border radius based on shape
      let radius;
      if (shape === "circle") {
        radius = "50%";
      } else if (shape === "square") {
        radius = "2px";
      } else {
        radius = "1px";
      }

      return {
        id: Math.random(),
        startX,
        color,
        size: shape === "rectangle" ? `${size}px ${size * 0.4}px` : `${size}px`,
        duration: `${pieceDuration}s`,
        delay: `${delay}s`,
        drift: `${drift}px`,
        rotation: `${rotation}deg`,
        opacity,
        radius,
      };
    };

    // Generate confetti pieces
    const confettiPieces = [];
    const piecesPerSide = pieceCount / 2;

    // Left side confetti
    for (let i = 0; i < piecesPerSide; i++) {
      const delay = (i / piecesPerSide) * spawnDuration;
      const velocityX = 1.2 + Math.random() * 0.8; // Positive for right movement
      confettiPieces.push(createConfettiPiece(0, velocityX, delay));
    }

    // Right side confetti
    for (let i = 0; i < piecesPerSide; i++) {
      const delay = (i / piecesPerSide) * spawnDuration;
      const velocityX = -(1.2 + Math.random() * 0.8); // Negative for left movement
      confettiPieces.push(createConfettiPiece(window.innerWidth, velocityX, delay));
    }

    setPieces(confettiPieces);
  }, []); // Only run once on mount

  return createPortal(
    <ConfettiContainer>
      {pieces.map((piece) => (
        <ConfettiPiece
          key={piece.id}
          style={{
            left: `${piece.startX}px`,
            bottom: `-20px`,
            "--color": piece.color,
            "--size": piece.size,
            "--duration": piece.duration,
            "--delay": piece.delay,
            "--drift": piece.drift,
            "--rotation": piece.rotation,
            "--opacity": piece.opacity,
            "--radius": piece.radius,
          }}
        />
      ))}
    </ConfettiContainer>,
    document.body
  );
};

export default Confetti;

